import { LoginModal } from "@/components/LoginModal";
import { useAuthContext } from "@/contexts/AuthContext";
import { appendUtmParams } from "@/utils/urlUtils";
import { ChevronRight, Instagram } from "lucide-react";
import { TikTokIcon } from "@/components/icons/TikTokIcon";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { SupplierFooter } from "./SupplierFooter";
import { FooterNewsletterSignup } from "./FooterNewsletterSignup";
interface FooterProps {
  supplierWebsite?: string | null;
  supplierName?: string | null;
  isClaimed?: boolean;
}
export const Footer = ({
  supplierWebsite,
  supplierName,
  isClaimed
}: FooterProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [showFullPhone, setShowFullPhone] = useState(false);
  const {
    user,
    userType,
    supplierProfile
  } = useAuthContext();

  // Render supplier-specific footer on /anslut-ditt-foretag and all sub-pages
  if (location.pathname.startsWith('/anslut-ditt-foretag')) {
    return <SupplierFooter />;
  }
  const formatSupplierWebsite = (website: string | null | undefined): string | null => {
    if (!website || typeof website !== 'string') {
      return null;
    }
    const trimmed = website.trim();
    if (!trimmed || trimmed.length === 0) {
      return null;
    }
    if (trimmed.includes(' ') || trimmed.length < 4) {
      return null;
    }
    let formatted = trimmed;
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      formatted = `https://${trimmed}`;
    }
    try {
      const url = new URL(formatted);
      if (!url.hostname.includes('.')) {
        return null;
      }
      return formatted;
    } catch (error) {
      console.warn('Invalid supplier website URL:', trimmed);
      return null;
    }
  };
  const socialIcons = [{
    icon: Instagram,
    link: "https://www.instagram.com/weddinglysverige/",
    name: "Instagram"
  }, {
    icon: TikTokIcon,
    link: "https://www.tiktok.com/@weddinglysverige?_r=1&_t=ZN-94onChqPKTe",
    name: "TikTok"
  }];
  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setLoginModalOpen(true);
    }
  };
  const handleMyAccountClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      return;
    }
    if (userType === 'customer') {
      navigate('/profile-settings');
    } else if (userType === 'supplier' && supplierProfile) {
      navigate(`/supplier/${supplierProfile.id}`);
    }
  };
  const handlePhoneClick = () => {
    setShowFullPhone(!showFullPhone);
  };
  return <footer className="w-full bg-white">
    <div className="max-w-[1400px] mx-auto px-[50px] pt-16 pb-8 max-sm:px-5 md:max-lg:px-10 lg:px-[50px]">
      <div className="flex flex-col lg:flex-row lg:items-stretch lg:gap-40 xl:gap-52 mb-12">
        <div className="flex flex-col w-full min-w-0 lg:w-1/2 lg:max-w-[520px] shrink-0 lg:justify-between lg:min-h-0">
          <FooterNewsletterSignup />
          <img
            src="/lovable-uploads/weddingly-footer-logo.webp"
            className="hidden lg:block w-[200px] h-auto shrink-0 mt-auto pt-6"
            alt="Weddingly logotyp - Din kompletta bröllopsplanerare online"
            width="190"
            height="55"
            loading="lazy"
          />
        </div>

        <div className="border-t border-gray-200 my-8 sm:my-10 lg:hidden" aria-hidden />

        <div className="flex-1 min-w-0 flex flex-col lg:pl-16 xl:pl-24 2xl:pl-32">
        <div className="grid grid-cols-2 gap-x-6 sm:gap-x-10 gap-y-8 sm:gap-y-10 md:max-lg:grid-cols-4 md:max-lg:gap-y-0 md:max-lg:!gap-x-3 w-full min-w-0">
          <div>
            <h3 className="text-[#212121] text-sm font-semibold uppercase tracking-wide mb-4">
              Viktiga länkar
            </h3>
            <nav>
              <ul className="space-y-3">
                {user && (
                  <li>
                    <a
                      href={userType === 'supplier' && supplierProfile ? `/supplier/${supplierProfile.id}` : '/profile-settings'}
                      onClick={handleMyAccountClick}
                      className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200 cursor-pointer"
                    >
                      Mitt konto
                    </a>
                  </li>
                )}
                {!user && (
                  <li>
                    <a
                      href="/auth"
                      onClick={handleLoginClick}
                      className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200 cursor-pointer"
                    >
                      Logga in
                    </a>
                  </li>
                )}
                <li><a href="/inspiration" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Inspiration & guider</a></li>
                <li><a href="/annonsera-hos-oss" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Annonsera hos oss</a></li>
                {(() => {
                  const formattedWebsite = formatSupplierWebsite(supplierWebsite);
                  return formattedWebsite ? <li>
                    <a href={appendUtmParams(formattedWebsite, 'weddingly', 'referral', 'supplier_page')} target="_blank" rel={isClaimed ? "noopener noreferrer" : "noopener noreferrer nofollow"} className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">
                      Leverantörens hemsida
                    </a>
                  </li> : null;
                })()}
              </ul>
            </nav>
          </div>

          <div>
            <h3 className="text-[#212121] text-sm font-semibold uppercase tracking-wide mb-4">
              Verktyg
            </h3>
            <nav>
              <ul className="space-y-3">
                <li><a href="/verktyg" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Alla verktyg</a></li>
                <li><a href="/verktyg/brollop-checklista-pdf" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Checklista</a></li>
                <li><a href="/verktyg/brollopsbudget-kalkylator" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Budget</a></li>
                <li><a href="/verktyg/gastlista-mall" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Gästlista</a></li>
                <li><a href="/verktyg/bordsplacering-mall" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Bordsplacering</a></li>
                <li><a href="/verktyg/tidslinje-brollopsdagen" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Körschema</a></li>
              </ul>
            </nav>
          </div>

          <div className="min-w-0">
            <h3 className="text-[#212121] text-sm font-semibold uppercase tracking-wide mb-4">
              Kontakt
            </h3>
            <address className="not-italic space-y-3">
              <p className="text-[#333] text-base">Kungsportsavenyen 18,<br />411 36 Göteborg, Sverige</p>
              <button type="button" className="text-[#333] text-base cursor-pointer hover:text-gray-600 transition-colors text-left bg-transparent border-none p-0 font-sans" onClick={handlePhoneClick} aria-label={showFullPhone ? "Dölj fullständigt telefonnummer" : "Visa fullständigt telefonnummer"} aria-expanded={showFullPhone}>
                {showFullPhone ? '+46 (0) 761603561' : <span>+46 (0) 7616035<span className="text-primary">(xx)</span></span>}
              </button>
              <p className="text-[#333] text-base">info@weddingly.se</p>
            </address>
          </div>

          <div>
            <h3 className="text-[#212121] text-sm font-semibold uppercase tracking-wide mb-4">
              Support
            </h3>
            <nav>
              <ul className="space-y-3">
                <li><a href="/privacy-policy" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Integritetspolicy</a></li>
                <li><a href="/cookie-settings" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Cookieinställningar</a></li>
                <li><a href="/terms-and-conditions" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Användarvillkor</a></li>
                {/* FAQ link removed - add back when FAQ page is created */}
                <li><a href="/leverantor-villkor" className="text-[#333] text-base font-normal hover:text-[#111] hover:scale-105 inline-block transition-all duration-200">Leverantörsvillkor</a></li>
              </ul>
            </nav>
          </div>
        </div>

        <img
          src="/lovable-uploads/weddingly-footer-logo.webp"
          className="lg:hidden w-[200px] h-auto block shrink-0 mt-8 sm:mt-10 mx-auto md:mx-0"
          alt="Weddingly logotyp - Din kompletta bröllopsplanerare online"
          width="190"
          height="55"
          loading="lazy"
        />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <div className="flex justify-between items-center max-sm:flex-col max-sm:gap-4">
          <div className="flex items-center gap-4">
            {socialIcons.map((social, index) => {
              const IconComponent = social.icon;
              return <a key={index} href={social.link} aria-label={`Besök oss på ${social.name}`} className="w-10 h-10 flex items-center justify-center rounded-full bg-[#d5c8b7] hover:bg-[#c5b5a3] transition-colors">
                <IconComponent className="w-5 h-5 text-white" />
              </a>;
            })}
          </div>
          <p className="text-[#666] text-sm font-normal">
            Copyright © weddingly.se 2025 | All right reserved
          </p>
        </div>
      </div>
    </div>

    <Link
      to="/anslut-ditt-foretag"
      className="group flex w-full items-center justify-center gap-2 bg-black px-[50px] py-4 text-sm font-semibold uppercase tracking-wide text-white/90 transition-colors duration-200 hover:bg-neutral-900 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white max-sm:px-5 md:max-lg:px-10 lg:px-[50px]"
    >
      <span className="inline-flex items-center gap-2 origin-center transition-all duration-200 group-hover:scale-105">
        Anslut som leverantör
        <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
      </span>
    </Link>

    <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
  </footer>;
};